import EmblaCarousel, { Autoplay } from './embla-carousel.esm.js';

if (!window.__hiraEmblaInit) {
  window.__hiraEmblaInit = true;

  const INSTANCES = new Map();
  const CONTROLLERS = new Map();
  const FOCUS_SKIP_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
  const WHEEL_THRESHOLD = 40;
  const WHEEL_COOLDOWN = 250;

  const boolAttr = (el, name, fallback = false) => {
    const v = el.getAttribute(name);
    if (v === null || v === '') return fallback;
    return v === 'true';
  };

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Scroll-ability, not slide count. Slides that all fit the viewport collapse
  // to a single snap: the carousel cannot move, so no control should offer to
  // move it. Slide count is the wrong proxy precisely in that case — and it is
  // a layout property, not a position one, so `reInit`/`resize` is enough to
  // keep every caller in sync.
  const canScroll = (api) => api.scrollSnapList().length > 1;

  const parseSlidesPerView = (raw) => {
    if (!raw) return { base: null, breakpoints: null };
    const trimmed = raw.trim();
    if (trimmed === 'auto') return { base: null, breakpoints: null };

    if (/^\d+$/.test(trimmed)) {
      return { base: parseInt(trimmed, 10), breakpoints: null };
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        const base = typeof parsed.default === 'number' ? parsed.default : 1;
        const breakpoints = {};
        for (const [key, val] of Object.entries(parsed)) {
          if (key === 'default') continue;
          if (typeof val === 'number') {
            breakpoints[key] = { slidesToScroll: val };
          }
        }
        return { base, breakpoints };
      }
    } catch (err) {
      console.warn('[hira-embla] Invalid slides-per-view JSON on', raw);
    }
    return { base: 1, breakpoints: null };
  };

  const buildOptions = (el) => {
    const options = {
      loop: boolAttr(el, 'data-embla-loop'),
      align: el.getAttribute('data-embla-align') || 'start',
      dragFree: boolAttr(el, 'data-embla-dragfree'),
    };

    const raw = el.getAttribute('data-embla-slides-per-view');
    const { base, breakpoints } = parseSlidesPerView(raw);
    if (base !== null) options.slidesToScroll = base;
    if (breakpoints && Object.keys(breakpoints).length > 0) {
      options.breakpoints = breakpoints;
    }

    return options;
  };

  const buildPlugins = (el) => {
    const plugins = [];
    const autoplay = boolAttr(el, 'data-embla-autoplay');
    if (autoplay && !prefersReducedMotion()) {
      const delay = parseInt(el.getAttribute('data-embla-autoplay-delay'), 10) || 4000;
      plugins.push(Autoplay({ delay, stopOnInteraction: false }));
    }
    return plugins;
  };

  const wireArrows = (el, api) => {
    const prev = el.querySelector('[data-embla-prev]');
    const next = el.querySelector('[data-embla-next]');
    if (!prev || !next) return;

    prev.addEventListener('click', () => api.scrollPrev());
    next.addEventListener('click', () => api.scrollNext());

    const update = () => {
      const scrollable = canScroll(api);
      prev.toggleAttribute('hidden', !scrollable);
      next.toggleAttribute('hidden', !scrollable);
      if (!scrollable || api.internalEngine().options.loop) return;
      prev.toggleAttribute('disabled', !api.canScrollPrev());
      next.toggleAttribute('disabled', !api.canScrollNext());
    };

    update();
    api.on('select', update);
    api.on('reInit', update);
    api.on('resize', update);
  };

  const wireDots = (el, api) => {
    const container = el.querySelector('[data-embla-dots-container]');
    if (!container) return;

    const dots = [];
    const build = () => {
      container.replaceChildren();
      dots.length = 0;
      const snaps = api.scrollSnapList();
      snaps.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hira-carousel__dot';
        dot.setAttribute('data-embla-dot', '');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.addEventListener('click', () => api.scrollTo(i));
        container.appendChild(dot);
        dots.push(dot);
      });
    };

    const select = () => {
      const idx = api.selectedScrollSnap();
      dots.forEach((dot, i) => dot.classList.toggle('is-selected', i === idx));
    };

    const sync = () => {
      const scrollable = canScroll(api);
      container.toggleAttribute('hidden', !scrollable);
      if (!scrollable) {
        container.replaceChildren();
        dots.length = 0;
        return;
      }
      build();
      select();
    };

    sync();
    api.on('reInit', sync);
    api.on('resize', sync);
    api.on('select', select);
  };

  const wireScrollbar = (el, api) => {
    const track = el.querySelector('.hira-carousel__scrollbar');
    const thumb = el.querySelector('[data-embla-scrollbar-thumb]');
    if (!track || !thumb) return;

    const sizeThumb = () => {
      const total = api.scrollSnapList().length || 1;
      const inView = api.slidesInView(true).length || 1;
      const width = Math.max(10, Math.min(100, (inView / total) * 100));
      thumb.style.setProperty('--hira-carousel-scrollbar-thumb-width', `${width}%`);
    };

    const positionThumb = () => {
      const progress = Math.max(0, Math.min(1, api.scrollProgress()));
      thumb.style.setProperty('--hira-carousel-scrollbar-thumb-progress', String(progress));
    };

    const update = () => {
      const scrollable = canScroll(api);
      track.toggleAttribute('hidden', !scrollable);
      if (!scrollable) return;
      sizeThumb();
      positionThumb();
    };

    update();
    api.on('reInit', update);
    api.on('scroll', positionThumb);
    api.on('resize', update);

    let dragging = false;
    let pointerId = null;

    const trackToProgress = (clientX) => {
      const rect = track.getBoundingClientRect();
      if (rect.width === 0) return 0;
      return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    };

    const onDown = (e) => {
      dragging = true;
      pointerId = e.pointerId;
      thumb.setPointerCapture(pointerId);
      const total = api.scrollSnapList().length;
      const progress = trackToProgress(e.clientX);
      api.scrollTo(Math.round(progress * (total - 1)));
    };

    const onMove = (e) => {
      if (!dragging) return;
      const total = api.scrollSnapList().length;
      const progress = trackToProgress(e.clientX);
      api.scrollTo(Math.round(progress * (total - 1)));
    };

    const onUp = (e) => {
      if (!dragging) return;
      dragging = false;
      if (pointerId !== null) thumb.releasePointerCapture(pointerId);
      pointerId = null;
    };

    thumb.addEventListener('pointerdown', onDown);
    thumb.addEventListener('pointermove', onMove);
    thumb.addEventListener('pointerup', onUp);
    thumb.addEventListener('pointercancel', onUp);
  };

  const wireWheel = (el, api, signal) => {
    const viewport = el.querySelector('.hira-carousel__viewport');
    if (!viewport || api.slideNodes().length <= 1) return;

    let accumulated = 0;
    let cooling = false;

    viewport.addEventListener(
      'wheel',
      (e) => {
        // Trackpads and Chrome's shift+wheel report horizontal intent as deltaX;
        // Firefox keeps shift+wheel on deltaY, so it is read explicitly.
        let delta = 0;
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) delta = e.deltaX;
        else if (e.shiftKey) delta = e.deltaY;
        if (delta === 0) return;

        // Claim the gesture so the browser does not turn it into back/forward nav.
        e.preventDefault();
        if (cooling) return;

        accumulated += delta;
        if (Math.abs(accumulated) < WHEEL_THRESHOLD) return;

        const forward = accumulated > 0;
        accumulated = 0;
        if (forward ? !api.canScrollNext() : !api.canScrollPrev()) return;

        if (forward) api.scrollNext();
        else api.scrollPrev();

        // Momentum keeps firing events long after the fingers lift; the cooldown
        // turns that tail into one slide instead of a burst.
        cooling = true;
        setTimeout(() => {
          cooling = false;
        }, WHEEL_COOLDOWN);
      },
      { passive: false, signal }
    );
  };

  const wireKeyboard = (el, api) => {
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    el.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLElement && FOCUS_SKIP_TAGS.has(e.target.tagName)) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        api.scrollPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        api.scrollNext();
      }
    });
  };

  const initOne = (el) => {
    if (el.hasAttribute('data-embla-inited')) return;

    const viewport = el.querySelector('.hira-carousel__viewport');
    if (!viewport) {
      console.warn('[hira-embla] Missing .hira-carousel__viewport on', el);
      return;
    }

    const options = buildOptions(el);
    const plugins = buildPlugins(el);
    const api = EmblaCarousel(viewport, options, plugins);

    const controller = new AbortController();
    INSTANCES.set(el, api);
    CONTROLLERS.set(el, controller);
    el.setAttribute('data-embla-inited', 'true');

    wireKeyboard(el, api);
    if (boolAttr(el, 'data-embla-wheel')) wireWheel(el, api, controller.signal);
    if (boolAttr(el, 'data-embla-arrows')) wireArrows(el, api);
    if (boolAttr(el, 'data-embla-dots')) wireDots(el, api);
    if (boolAttr(el, 'data-embla-scrollbar')) wireScrollbar(el, api);

    // Dispatched last so a listener can call any api method synchronously.
    // Consumers that upgrade before this module runs rely on it; consumers that
    // upgrade after read window.hiraEmbla.get(el) instead.
    el.dispatchEvent(new CustomEvent('hira:embla-init', { bubbles: true, detail: { api } }));
  };

  const scanWithin = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    const roots = root.querySelectorAll('[data-embla]:not([data-embla-inited])');
    for (const el of roots) initOne(el);
    if (root.matches && root.matches('[data-embla]:not([data-embla-inited])')) {
      initOne(root);
    }
  };

  const scanAll = () => scanWithin(document);

  const destroyOne = (el) => {
    if (!el || typeof el.removeAttribute !== 'function') return;
    INSTANCES.get(el)?.destroy();
    INSTANCES.delete(el);
    CONTROLLERS.get(el)?.abort();
    CONTROLLERS.delete(el);
    el.removeAttribute('data-embla-inited');
  };

  const destroyWithin = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    const roots = root.querySelectorAll('[data-embla-inited]');
    for (const el of roots) destroyOne(el);
  };

  // Assigned before the first scan so a hira:embla-init listener firing during
  // that scan can already resolve the facade.
  window.hiraEmbla = {
    init: (el) => {
      if (!el || typeof el.querySelector !== 'function') return;
      initOne(el);
    },
    destroy: destroyOne,
    get: (el) => (el ? INSTANCES.get(el) : undefined),
    scan: scanWithin,
  };

  document.addEventListener('DOMContentLoaded', scanAll);
  document.addEventListener('shopify:section:load', (e) => scanWithin(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroyWithin(e.target));

  if (document.readyState !== 'loading') scanAll();
}
